import { useTranslation } from 'react-i18next'
import { UserPlus, X } from 'lucide-react'
import AsyncSelect from 'react-select/async'
import UserAvatar from '@components/UserAvatar'
import { PermissionLevel, UserOption, PermissionUser } from './types'
import { CustomOption, CustomSingleValue, customSelectStyles, formatUserName } from './UserSelectComponents'

interface PermissionsTableProps {
  allPermissionUsers: PermissionUser[]
  permissions: Map<string, PermissionLevel>
  loadUsers: (inputValue: string) => Promise<UserOption[]>
  handleAddUser: (option: UserOption | null) => void
  handlePermissionChange: (userId: string, level: PermissionLevel) => void
  handleRemoveUser: (permissionId: string | number, userId: string) => void
}

export default function PermissionsTable({
  allPermissionUsers,
  permissions,
  loadUsers,
  handleAddUser,
  handlePermissionChange,
  handleRemoveUser
}: PermissionsTableProps) {
  const { t } = useTranslation()

  // Permission level options
  const permissionLevelOptions: { value: PermissionLevel; label: string }[] = [
    { value: 'read', label: t('permissions.levels.read') },
    { value: 'write', label: t('permissions.levels.write') },
    { value: 'admin', label: t('permissions.levels.admin') }
  ]

  return (
    <div className="card bg-base-200 p-4">
      <h4 className="font-semibold text-lg mb-4">{t('permissions.manageUsers.title')}</h4>

      {/* Add User */}
      <div className="mb-4">
        <label className="label">
          <span className="label-text font-medium flex items-center gap-2">
            <UserPlus className="w-4 h-4" />
            {t('permissions.manageUsers.addUser')}
          </span>
        </label>
        <AsyncSelect
          cacheOptions
          loadOptions={loadUsers}
          onChange={handleAddUser}
          placeholder={t('permissions.manageUsers.searchPlaceholder')}
          noOptionsMessage={() => t('permissions.noResults')}
          loadingMessage={() => t('permissions.searching')}
          styles={customSelectStyles}
          menuPortalTarget={document.body}
          classNames={{
            menu: () => 'bg-base-100',
            menuList: () => 'bg-base-100'
          }}
          components={{
            Option: CustomOption,
            SingleValue: CustomSingleValue
          }}
          value={null}
          key={permissions.size} // Reset when permissions change
        />
      </div>

      {/* Current Permissions Table */}
      <div>
        <h5 className="font-medium mb-3">{t('permissions.manageUsers.currentPermissions')}</h5>

        {allPermissionUsers.length === 0 ? (
          <div className="text-center py-8 text-base-content/70 bg-base-100 rounded-lg">
            {t('permissions.manageUsers.noPermissions')}
          </div>
        ) : (
          <div className="overflow-x-auto bg-base-100 rounded-lg">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>{t('permissions.manageUsers.user')}</th>
                  <th>{t('permissions.manageUsers.permissionLevel')}</th>
                  <th className="text-right">{t('permissions.manageUsers.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {allPermissionUsers.map(({ permissionId, user, currentLevel, inherited }) => (
                  <tr key={user.id}>
                    <td>
                      <div className="flex items-center gap-3">
                        <UserAvatar user={user} size="sm" showTooltip={false} />
                        <div>
                          <div className="font-medium">{formatUserName(user)}</div>
                          <div className="text-sm text-base-content/70">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <select
                        className="select select-bordered select-sm"
                        value={currentLevel}
                        onChange={(e) => handlePermissionChange(user.id, e.target.value as PermissionLevel)}
                        disabled={inherited}
                      >
                        {permissionLevelOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      {inherited && (
                        <span className="badge badge-sm badge-ghost ml-2">{t('permissions.manageUsers.inherited')}</span>
                      )}
                    </td>
                    <td className="text-right">
                      <button
                        onClick={() => handleRemoveUser(permissionId, user.id)}
                        className="btn btn-ghost btn-sm text-error"
                        disabled={inherited}
                      >
                        <X className="w-4 h-4" />
                        {t('permissions.manageUsers.remove')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
